import { z } from 'zod';
import { KitDestructionDocumentDtoSchema } from './kit-destruction.schema.js';
import { PatientTodoDocumentDtoSchema } from './patient-todo.schema.js';
import { VclDocumentDtoSchema } from './vcl.schema.js';
// ---------------------------------------------------------------------------
// PDF transport (Domain 7 — Document & PDF Engine, issue #10 atom 5)
// ---------------------------------------------------------------------------
/**
 * BullMQ queue for server-side document rendering (ADR-010: Playwright in the
 * worker, never in the API process). ONE queue for all document types — the
 * job name selects the template (`vcl-render`, `kit-destruction-render` and
 * `patient-todo-render`).
 */
export const DOCUMENT_PDF_QUEUE = 'document-pdf';
/** Job name of the VCL A4 render (REQ-DOC-007/008). */
export const VCL_RENDER_JOB = 'vcl-render';
/**
 * Payload of a `vcl-render` job. The API composes the full document (atom 4)
 * and hands it over as data — the worker NEVER reads the DB on the render
 * path, so tenant isolation holds by construction; `tenantId` still travels
 * because every job payload must carry it (worker rule, skill tenant-rls) and
 * structured logs need it.
 */
export const VclRenderJobSchema = z.object({
    tenantId: z.number().int().positive(),
    document: VclDocumentDtoSchema,
});
/**
 * Successful job result: the rendered PDF as base64. The handoff is transient
 * through Redis (atom-5 decision: no file archive — REQ-DOC-007 requires
 * server-side GENERATION, not storage); the API validates this shape before
 * streaming the bytes to the caller.
 */
export const VclRenderResultSchema = z.object({
    pdfBase64: z.string().min(1),
});
/** Job name of the kit destruction protocol A4 render (REQ-DOC-018, issue #31 B1). */
export const KIT_DESTRUCTION_RENDER_JOB = 'kit-destruction-render';
/**
 * Payload of a `kit-destruction-render` job. Same delegation contract as VCL:
 * the API composes the full document (D7 calls the D5 read doors) and the
 * worker NEVER reads the DB on the render path — tenant isolation holds by
 * construction; `tenantId` still travels for the worker rule and structured
 * logs.
 */
export const KitDestructionRenderJobSchema = z.object({
    tenantId: z.number().int().positive(),
    document: KitDestructionDocumentDtoSchema,
});
/** Successful job result: the rendered PDF as base64 (same transient Redis handoff as VCL). */
export const KitDestructionRenderResultSchema = z.object({
    pdfBase64: z.string().min(1),
});
/** Job name of the patient todo-list card A4 render (REQ-DOC-017, issue #31 B2). */
export const PATIENT_TODO_RENDER_JOB = 'patient-todo-render';
/**
 * Payload of a `patient-todo-render` job. Same delegation contract as VCL and
 * the destruction protocol: the API composes the full card (D7 calls the D3/D4
 * read doors) and the worker NEVER reads the DB on the render path — tenant
 * isolation holds by construction; `tenantId` still travels for the worker
 * rule and structured logs.
 */
export const PatientTodoRenderJobSchema = z.object({
    tenantId: z.number().int().positive(),
    document: PatientTodoDocumentDtoSchema,
});
/** Successful job result: the rendered PDF as base64 (same transient Redis handoff as VCL). */
export const PatientTodoRenderResultSchema = z.object({
    pdfBase64: z.string().min(1),
});
//# sourceMappingURL=pdf.schema.js.map